/*
    NOTE: THIS IS A LEGACY COMPONENT, ONLY USED FOR THE OLD PRE VOYAGE MANAGEMENT PLANS. SHOULD BE REMOVED WHEN THOSE
    PLANS ARE MIGRATED OR OTHERWISE NOT NEEDED.
*/

import {
  PlanModelV1,
  ReceiptPlanModelV1,
  TankPlanModel,
} from "@/api/dischargePlan/types";
import { defineStore } from "pinia";
import { onMounted, ref, watch, computed } from "vue";
import api from "@/api/dischargePlan";
import { useRoute, useRouter } from "vue-router";
import { ReceiptPlanInfo } from "@/models/receiptPlan/receiptPlanInfo";
import { parseDatetime } from "@/helpers/date";
import { ROUTE_NAMES } from "@/router/routes";
import { useDischargePlanStore } from "../dischargePlan";

/**
 * A store which holds shared state related to the currently active dischargePlan
 */
export const useDischargePlanStoreV1 = defineStore("dischargePlanV1", () => {
  const route = useRoute();
  const router = useRouter();
  const v2DischargePlanStore = useDischargePlanStore();

  const activePlan = ref<PlanModelV1>();
  const activePlanId = ref<number | undefined>(
    route.params?.planId ? parseInt(route.params.planId as string) : undefined,
  );

  const activeReceiptPlans = computed(() =>
    activePlan.value?.receiptPlans != null
      ? Object.values(activePlan.value?.receiptPlans)
      : [],
  );

  const activeTanks = computed(() =>
    activeReceiptPlans.value.reduce(
      (tanks, receiptPlan) =>
        tanks.concat(
          Object.values(receiptPlan.tankPlans).filter(
            (tank) => !!tank.plannedReceipt || !!tank.pipelineAdjustments,
          ),
        ),
      [] as TankPlanModel[],
    ),
  );

  function getTankPlan(tankPlanId: number) {
    const foundTanks = activeTanks.value.filter(
      (tank) => tank.id == tankPlanId,
    );
    if (foundTanks.length != 1) {
      return null;
    }
    return foundTanks[0];
  }

  // Updates the plan data from the server
  async function RefreshPlanData() {
    if (!activePlanId.value) {
      activePlan.value = undefined;
      return;
    }
    activePlan.value = await api.getPlanForPlanIdV1(activePlanId.value);

    // For the sake of compatibility when some components
    // use the V2 store, and some use V1, this will update
    // The new store for these cases.
    await v2DischargePlanStore.RefreshPlanData();
  }

  // Sets the plan id based on the id in the path
  function setPlanIdFromPath() {
    if (route.params.planId) {
      activePlanId.value = parseInt(route.params.planId as string);
    } else {
      activePlanId.value = undefined;
    }
  }

  async function UpdateReceiptPlans(reciptPlans: ReceiptPlanModelV1[]) {
    if (!activePlan.value) {
      throw new Error("No plan to update");
    }
    if (activePlan.value.id == null) {
      throw new Error("Plan has no id");
    }

    const reciptPlansDict: Record<string, ReceiptPlanModelV1> = {};
    for (const plan of reciptPlans) {
      if (plan.id == null) {
        continue;
      }
      reciptPlansDict[plan.id.toString()] = plan;
    }

    activePlan.value.receiptPlans = reciptPlansDict;
    await api.updatePlanV1(activePlan.value);
    await RefreshPlanData();
  }

  async function UpdatePlan(plan: ReceiptPlanInfo) {
    if (!activePlan.value) {
      throw new Error("No plan to update");
    }
    if (activePlan.value.id == null) {
      throw new Error("Plan has no id");
    }

    activePlan.value.vessel = plan.vessel;
    activePlan.value.pilotOnBoard = parseDatetime(plan.pilotOnBoard);
    activePlan.value.voyage = plan.voyage;
    if (plan.terminalName != null) {
      activePlan.value.terminalName = plan.terminalName;
    }

    await api.updatePlanV1(activePlan.value);
    await RefreshPlanData();
  }

  async function UpdateTankPlan(receiptPlanId: number, plan: TankPlanModel) {
    if (!activePlan.value) {
      throw new Error("No plan to update");
    }
    if (activePlan.value.id == null) {
      throw new Error("Plan has no id");
    }

    if (plan.id == null) {
      throw new Error("Tank plan has no id");
    }

    activePlan.value.receiptPlans[receiptPlanId.toString()].tankPlans[
      plan.id.toString()
    ] = plan;

    await api.updatePlanV1(activePlan.value);
    await RefreshPlanData();
  }

  async function CreatePlan(plan: ReceiptPlanInfo) {
    if (plan.terminalId == null) {
      throw new Error("A terminal id is required");
    }

    const newPlanId = await api.createPlanV1({
      pilotOnBoard: parseDatetime(plan.pilotOnBoard),
      terminalId: plan.terminalId,
      vessel: plan.vessel,
      voyage: plan.voyage,
    });

    router.push({
      name: ROUTE_NAMES.VESSEL_INFO,
      params: { planId: newPlanId },
    });
  }

  async function AbandonPlan(planId: number) {
    await api.abandonPlan(planId);
  }

  async function ReactivatePlan(planId: number) {
    await api.reactivatePlan(planId);
  }

  watch(activePlanId, RefreshPlanData);

  // Keep the planId set based on the route
  watch(route, setPlanIdFromPath);

  // Load the plan data on first load
  onMounted(RefreshPlanData);

  return {
    activePlanId,
    activePlan,
    getTankPlan,
    UpdatePlan,
    UpdateTankPlan,
    UpdateReceiptPlans,
    RefreshPlanData,
    CreatePlan,
    AbandonPlan,
    ReactivatePlan,
  };
});
